import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import RecitationAudioPlayer from "../components/RecitationAudioPlayer";
import { Clock, CheckCircle2, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";

export default function MySubmissions() {
  const { t } = useLanguage();
  const [subs, setSubs] = useState(null);
  useEffect(() => {
    api.get("/recitation/submissions/mine")
      .then((r) => setSubs(r.data))
      .catch((e) => { toast.error(t("mySubmissions.couldnTLoadYourSubmissions")); setSubs([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (subs === null) {
    return (
      <div className="flex items-center gap-2 text-forest-700" data-testid="submissions-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> {t("mySubmissions.loadingYourSubmissions")}
      </div>
    );
  }

  const pending = subs.filter((s) => s.status === "pending");
  const graded = subs.filter((s) => s.status === "graded");

  return (
    <div className="space-y-8 max-w-5xl" data-testid="my-submissions-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{t("mySubmissions.mySubmissions")}</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{t("mySubmissions.sentToMyTeacher")}</h1>
        <p className="mt-2 text-foreground/70">{t("mySubmissions.recitationsYouSubmittedForGrading")}</p>
      </header>

      <section>
        <h2 className="font-display font-semibold text-lg text-forest-700 flex items-center gap-2">
          <Clock className="h-4 w-4 text-gold-600" /> {t("mySubmissions.pendingReview", { pendingCount: pending.length })}
        </h2>
        <div className="mt-4 grid gap-3">
          {pending.length === 0 ? (
            <div className="hifz-card text-sm text-muted-foreground" data-testid="no-pending">{t("mySubmissions.nothingPending")}</div>
          ) : pending.map((s) => (
            <article key={s.submission_id} className="hifz-card" data-testid={`pending-${s.submission_id}`}>
              <div className="flex flex-wrap justify-between gap-2 items-start">
                <div>
                  <div className="font-display font-semibold text-forest-700">{t("mySubmissions.surahAyah", { surah: s.surah, ayah: s.ayah })}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{t("mySubmissions.sentOn", { className: s.class_name, date: new Date(s.submitted_at).toLocaleString() })}</div>
                </div>
                <span className="status-pill status-learning">{t("mySubmissions.awaitingTeacher")}</span>
              </div>
              <div className="mt-3 text-xs text-muted-foreground flex flex-wrap gap-3">
                <span>{t("mySubmissions.aiAccuracy")} <strong className="text-forest-700">{s.ai_accuracy}%</strong></span>
                <span>{t("mySubmissions.aiTajweed")} <strong className="text-gold-600">{s.ai_tajweed}%</strong></span>
              </div>
              {s.student_note && <p className="mt-3 text-sm italic text-foreground/75">"{s.student_note}"</p>}
              <div className="mt-4"><RecitationAudioPlayer attemptId={s.attempt_id} testid={`audio-${s.submission_id}`} /></div>
            </article>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display font-semibold text-lg text-forest-700 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-forest-700" /> {t("mySubmissions.graded", { gradedCount: graded.length })}
        </h2>
        <div className="mt-4 grid gap-3">
          {graded.length === 0 ? (
            <div className="hifz-card text-sm text-muted-foreground" data-testid="no-graded">{t("mySubmissions.noGradedRecitationsYet")}</div>
          ) : graded.map((s) => (
            <article key={s.submission_id} className="hifz-card" data-testid={`graded-${s.submission_id}`}>
              <div className="flex flex-wrap justify-between gap-2 items-start">
                <div>
                  <div className="font-display font-semibold text-forest-700">{t("mySubmissions.surahAyah", { surah: s.surah, ayah: s.ayah })}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {t("mySubmissions.gradedBy", { className: s.class_name, teacher: s.graded_by_name || t("mySubmissions.teacherDefault"), date: new Date(s.graded_at).toLocaleDateString() })}
                  </div>
                </div>
                <div className="flex items-center gap-1" data-testid={`stars-${s.submission_id}`}>
                  {[1,2,3,4,5].map((n) => (
                    <Star key={n} className={`h-5 w-5 ${n <= s.teacher_grade ? "text-gold-500 fill-gold-500" : "text-sand-300"}`} />
                  ))}
                </div>
              </div>
              {s.teacher_note && (
                <div className="mt-4 p-4 rounded-xl bg-forest-600/8 border-l-2 border-forest-600">
                  <div className="text-xs uppercase tracking-[0.18em] text-forest-700 font-semibold mb-1">{t("mySubmissions.teacherSNote")}</div>
                  <p className="text-sm text-foreground/85">{s.teacher_note}</p>
                </div>
              )}
              <div className="mt-4"><RecitationAudioPlayer attemptId={s.attempt_id} testid={`audio-${s.submission_id}`} /></div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
